import { useState } from 'react'

import { Box, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'
import { GetModuleAvailabilitiesDocument } from 'generated/resources/graphql'

import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface CompletionConfigurationData {
  attendancePercentage?: number
  moduleCompletedPercentage?: number
  knowledgeTestRequired: boolean
}

interface CompletionConfigurationFieldsProps {
  data: CompletionConfigurationData
  onDataChanged: (data: CompletionConfigurationData) => void
  readonly?: boolean
  isScormTraining?: boolean
}

export const CompletionConfigurationFields = ({
  data,
  onDataChanged,
  readonly = false,
  isScormTraining = false,
}: CompletionConfigurationFieldsProps) => {
  const isAttendanceEnabled = data.attendancePercentage !== undefined
  const isModuleCompletionEnabled = data.moduleCompletedPercentage !== undefined
  const [isFocused, setIsFocused] = useState(false)

  const { company } = useCurrentSession()

  const { data: featureData } = useQuery(GetModuleAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      ids: [AvailableModules.lms],
      companyId: company.id,
    },
  })

  const moduleAvailabilities = featureData?.companies.moduleAvailabilities ?? []
  const hasLMSEntitlement = isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.lms)
  const isModuleCompletionAvailable = hasLMSEntitlement && !isScormTraining

  return (
    <Box gap="s16">
      {/* Module Completed Percentage */}
      <Box
        gap="s12"
        padding="s16"
        border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
      >
        <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween">
          <Box gap="s4" flexDirection="column">
            <Text
              size="300"
              weight="medium"
              color={isModuleCompletionAvailable ? 'grey1000' : 'grey600'}
            >
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.module_completed_percentage'
              )}
            </Text>
            <Text
              size="300"
              weight="normal"
              color={isModuleCompletionAvailable ? 'grey800' : 'grey600'}
            >
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.module_completed_percentage_helper'
              )}
            </Text>
          </Box>
          <TogglerCheckbox
            value={isModuleCompletionAvailable ? isModuleCompletionEnabled : false}
            onChange={
              isModuleCompletionAvailable
                ? (checked: boolean) =>
                    onDataChanged({
                      ...data,
                      moduleCompletedPercentage: checked ? 100 : undefined,
                    })
                : () => {}
            }
            disabled={!isModuleCompletionAvailable || readonly}
          />
        </Box>
        {!hasLMSEntitlement && (
          <Box paddingY="s4" width="min-content">
            <UpsellingKit.UpsellButton
              label={i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.learn_more_lms'
              )}
              requestSource="trainings-lms"
              pageSource="trainings-lms"
              requestReason={ValidReasons.MissingFeature}
              moduleRequested={AvailableModules.lms}
              variant="outlinePromote"
              showModuleModal
            />
          </Box>
        )}
      </Box>

      {/* Attendance Percentage */}
      <Box
        gap="s12"
        padding="s16"
        border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
      >
        <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween">
          <Box gap="s4" flexDirection="column">
            <Text size="300" weight="medium" color="grey1000">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.attendance_percentage'
              )}
            </Text>
            <Text size="300" weight="normal" color="grey800">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.attendance_percentage_field_description'
              )}
            </Text>
          </Box>
          <TogglerCheckbox
            value={isAttendanceEnabled}
            onChange={(checked: boolean) =>
              onDataChanged({
                ...data,
                attendancePercentage: checked ? 100 : undefined,
              })
            }
            disabled={readonly}
          />
        </Box>
        {isAttendanceEnabled && (
          <Box gap="s4">
            <Text size="300" weight="medium" color="grey1000">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.attendance_percentage_field_title'
              )}
            </Text>
            <Text size="300" weight="normal" color="grey800">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.attendance_percentage_helper'
              )}
            </Text>
            <TextField
              type={isFocused ? 'number' : 'text'}
              value={
                isFocused
                  ? String(data.attendancePercentage ?? '')
                  : data.attendancePercentage !== undefined
                    ? `${data.attendancePercentage}%`
                    : ''
              }
              onChange={(value) => {
                const numValue = value.replace('%', '').trim()
                const parsed = parseInt(numValue, 10)
                if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
                  onDataChanged({ ...data, attendancePercentage: parsed })
                } else if (numValue === '') {
                  onDataChanged({ ...data, attendancePercentage: 0 })
                }
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              readonly={readonly}
            />
          </Box>
        )}
      </Box>

      {/* Knowledge Test Required */}
      <Box
        gap="s12"
        padding="s16"
        border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
      >
        <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween">
          <Box gap="s4" flexDirection="column">
            <Text size="300" weight="medium" color="grey1000">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.knowledge_test_required'
              )}
            </Text>
            <Text size="300" weight="normal" color="grey800">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.completion_configuration.knowledge_test_required_helper'
              )}
            </Text>
          </Box>
          <TogglerCheckbox
            value={data.knowledgeTestRequired}
            onChange={(checked: boolean) =>
              onDataChanged({ ...data, knowledgeTestRequired: checked })
            }
            disabled={readonly}
          />
        </Box>
      </Box>
    </Box>
  )
}
